import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import {
  MapPinIcon,
  CalendarDaysIcon,
  UserIcon,
  CreditCardIcon,
  ShoppingBagIcon,
  ClipboardDocumentCheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

// Redux
import { resetCreateOrderFlow } from '../../redux/Reducers/CreateOrderReducer';

// Utils
import { dateFn } from '../../utils/dateTimeFormatter';

// Step components
import StepOrderType from './StepOrderType';
import StepPickupAddress from './StepPickupAddress';
import StepServices from './StepServices';
import StepProvider from './StepProvider';
import StepDelivery from './StepDelivery';
import StepPayment from './StepPayment';
import StepReview from './StepReview';
import OrderConfirmation from './OrderConfirmation';
import EstimateSidebar from './EstimateSidebar';

// ─── localStorage persistence key ──────────────────────────────────────────────
const LS_KEY = 'createOrderV2_draft';

function saveDraft(data) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch {}
}
function loadDraft() {
  try {
    const d = localStorage.getItem(LS_KEY);
    return d ? JSON.parse(d) : null;
  } catch {
    return null;
  }
}
function clearDraft() {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {}
}

// ─── Accordion Step Wrapper ────────────────────────────────────────────────────
function AccordionStep({
  stepNumber,
  title,
  icon: Icon,
  isOpen,
  isCompleted,
  isEnabled,
  summaryContent,
  onToggle,
  onEdit,
  children,
}) {
  const bodyRef = useRef(null);
  return (
    <div
      className={`mb-3 overflow-hidden rounded-xl border transition-all duration-200 ${
        isOpen
          ? 'border-client-300 bg-white shadow-md'
          : isCompleted
          ? 'border-green-200 bg-white'
          : 'border-slate-200 bg-slate-50/80'
      }`}
    >
      <button
        type='button'
        className={`flex w-full items-center gap-3 px-4 py-3 text-left sm:px-5 ${
          isEnabled ? 'cursor-pointer hover:bg-slate-50/50' : 'cursor-not-allowed opacity-50'
        }`}
        onClick={() => isEnabled && onToggle()}
        disabled={!isEnabled}
      >
        <div
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
            isCompleted
              ? 'bg-green-500 text-white'
              : isOpen
              ? 'bg-client-300 text-white'
              : 'bg-slate-200 text-slate-500'
          }`}
        >
          {isCompleted ? <CheckCircleIcon className='h-4 w-4' /> : stepNumber}
        </div>
        <Icon
          className={`h-4 w-4 shrink-0 ${
            isCompleted ? 'text-green-600' : isOpen ? 'text-client-300' : 'text-slate-400'
          }`}
        />
        <div className='min-w-0 flex-1'>
          <span
            className={`text-sm font-semibold ${
              isCompleted ? 'text-green-700' : isOpen ? 'text-slate-900' : 'text-slate-500'
            }`}
          >
            {title}
          </span>
          {!isOpen && summaryContent && <p className='mt-0.5 truncate text-xs text-slate-500'>{summaryContent}</p>}
        </div>
        {!isOpen && isCompleted && (
          <span
            className='text-client-300 shrink-0 cursor-pointer text-xs font-semibold'
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            Change
          </span>
        )}
        {isOpen ? (
          <ChevronUpIcon className='h-4 w-4 shrink-0 text-slate-400' />
        ) : (
          <ChevronDownIcon className='h-4 w-4 shrink-0 text-slate-400' />
        )}
      </button>
      <div
        ref={bodyRef}
        className={`transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[8000px] opacity-100' : 'max-h-0 overflow-hidden opacity-0'
        }`}
      >
        <div className='border-t border-slate-100 px-4 py-4 sm:px-5'>{children}</div>
      </div>
    </div>
  );
}

function ContinueBtn({ disabled, onClick, label = 'Continue' }) {
  return (
    <div className='mt-4 flex justify-end'>
      <button
        type='button'
        className={`rounded-full px-6 py-2 text-sm font-semibold text-white transition ${
          disabled ? 'cursor-not-allowed bg-slate-300' : 'bg-client-300 hover:bg-client-400 shadow'
        }`}
        onClick={onClick}
        disabled={disabled}
      >
        {label}
      </button>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────────
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default function CreateNewOrderV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const createNewOrder$ = useSelector((s) => s.createNewOrder);
  const {
    orderType,
    orderServiceAddress,
    selectedServices,
    orderTimeSlots,
    orderRepeatFrequency,
    orderProvider,
    orderPaymentDetails,
    orderPreferences,
  } = createNewOrder$;
  const { profileData } = useSelector((s) => s.profile);

  const [activeStep, setActiveStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [orderConfirmed, setOrderConfirmed] = useState(false);

  // Timer
  const [count, setCount] = useState(1800);
  const timerRef = useRef(null);
  const expiryRef = useRef(null);

  // ── Profile guards ────────────────────────────────────────────────
  useEffect(() => {
    if (profileData?.status !== 'ACTIVE') {
      toast.error('Your account is inactive!');
      navigate('/dashboard');
    } else if (!profileData?.is_allowed_to_create_order) {
      toast.error('Past due invoice. Make a payment to order.');
      navigate('/account', { state: { from: 'payments' } });
    }
  }, [profileData?.status]);

  // ── Always reset on mount (page load / refresh) ────────────────────
  useEffect(() => {
    clearDraft();
    dispatch(resetCreateOrderFlow());
  }, []);

  // ── Persist draft to localStorage on state change ─────────────────
  useEffect(() => {
    if (orderType) {
      saveDraft({
        orderType,
        orderServiceAddress,
        selectedServices,
        orderTimeSlots,
        orderRepeatFrequency,
        orderProvider,
        orderPaymentDetails,
        orderPreferences,
        final_tip: createNewOrder$.final_tip,
      });
    }
  }, [
    orderType,
    orderServiceAddress,
    selectedServices,
    orderTimeSlots,
    orderRepeatFrequency,
    orderProvider,
    orderPaymentDetails,
    orderPreferences,
    createNewOrder$.final_tip,
  ]);

  // ── Timer ─────────────────────────────────────────────────────────
  useEffect(() => {
    const clear = () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      expiryRef.current = null;
    };
    if (!isEmpty(orderTimeSlots) && activeStep >= 4) {
      if (!expiryRef.current) expiryRef.current = Date.now() + 1800_000;
      const calc = () => Math.max(0, Math.round((expiryRef.current - Date.now()) / 1000));
      setCount(calc());
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        const r = calc();
        setCount(r);
        if (r <= 0) clear();
      }, 1000);
    } else clear();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [orderTimeSlots, activeStep]);

  useEffect(() => {
    if (count === 300) toast.error('Session expiring soon. Complete your order.');
    else if (count === 0) {
      setActiveStep(1);
      setCompletedSteps(new Set());
      clearDraft();
      toast.error('Session expired. Please start over.');
      setCount(1800);
    } else if (activeStep <= 3) setCount(1800);
  }, [count]);

  // ── Step helpers ──────────────────────────────────────────────────
  const next = (step) => {
    setCompletedSteps((prev) => new Set([...prev, step]));
    setActiveStep(step + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const edit = (step) => {
    setActiveStep(step);
    setCompletedSteps((prev) => {
      const n = new Set(prev);
      for (let i = step; i <= 7; i++) n.delete(i);
      return n;
    });
  };
  const toggle = (step) => setActiveStep(activeStep === step ? 0 : step);
  const isEnabled = (step) => {
    if (step === 1) return true;
    for (let i = 1; i < step; i++) if (!completedSteps.has(i)) return false;
    return true;
  };

  // Only mark green when user has explicitly clicked Continue
  const isStepComplete = (step) => completedSteps.has(step);

  const getSummary = (step) => {
    switch (step) {
      case 1:
        return orderType === 'oneTime'
          ? 'One-time'
          : `Subscription${orderRepeatFrequency?.name ? ` · ${orderRepeatFrequency.name}` : ''}`;
      case 2:
        return orderServiceAddress?.full_address || '';
      case 3:
        return selectedServices?.length
          ? `${selectedServices.length} service${selectedServices.length > 1 ? 's' : ''} selected`
          : '';
      case 4: {
        const name = orderProvider?.id ? `${orderProvider.first_name} ${orderProvider.last_name}` : 'Auto Match';
        const dt = orderTimeSlots?.pickup_date || orderTimeSlots?.first_pickup_date;
        return dt
          ? `${name} · ${dateFn(
              'toLocal',
              dt,
              orderTimeSlots?.pickup_start_time,
              orderTimeSlots?.pickup_end_time,
              orderServiceAddress?.time_zone
            )}`
          : name;
      }
      case 5:
        return orderTimeSlots?.dropoff_date
          ? dateFn(
              'toLocal',
              orderTimeSlots.dropoff_date,
              orderTimeSlots.dropoff_start_time,
              orderTimeSlots.dropoff_end_time,
              orderServiceAddress?.time_zone
            )
          : '';
      case 6: {
        const t = orderPaymentDetails?.billing_details?.default_payment_method_type;
        return t === 'ACH' ? 'Bank (ACH)' : t === 'CHECK' ? 'Check' : t === 'STRIPE' ? 'Card' : '';
      }
      default:
        return '';
    }
  };

  const minutes = Math.floor(count / 60);
  const seconds = count % 60;
  const showTimer = !isEmpty(orderTimeSlots) && activeStep >= 4;

  if (orderConfirmed)
    return (
      <div className='px-4 py-8 sm:mx-auto sm:max-w-5xl sm:px-6'>
        <OrderConfirmation />
      </div>
    );

  const steps = [
    { n: 1, title: 'Order Type', icon: ArrowPathIcon },
    { n: 2, title: 'Pickup Address', icon: MapPinIcon },
    { n: 3, title: 'Services', icon: ShoppingBagIcon },
    { n: 4, title: 'Provider & Pickup', icon: UserIcon },
    { n: 5, title: 'Delivery', icon: CalendarDaysIcon },
    { n: 6, title: 'Payment', icon: CreditCardIcon },
    { n: 7, title: 'Review & Confirm', icon: ClipboardDocumentCheckIcon },
  ];

  return (
    <div className='min-h-screen bg-slate-50/50'>
      <div className='px-4 py-4 sm:mx-auto sm:max-w-6xl sm:px-6 lg:py-6'>
        {/* Header */}
        <div className='mb-4 flex items-center justify-between'>
          <div>
            <h1 className='text-xl font-bold text-slate-900 sm:text-2xl'>Create New Order</h1>
            <p className='text-xs text-slate-500'>Complete each section below</p>
          </div>
          {showTimer && (
            <div
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
                count <= 300 ? 'bg-red-100 text-red-700' : 'text-client-300 bg-blue-50'
              }`}
            >
              <svg className='h-3.5 w-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              {minutes}:{seconds.toString().padStart(2, '0')}
            </div>
          )}
        </div>

        {/* Layout */}
        <div className='flex flex-col gap-4 lg:flex-row'>
          <div className='min-w-0 flex-1'>
            {steps.map((s) => (
              <AccordionStep
                key={s.n}
                stepNumber={s.n}
                title={s.title}
                icon={s.icon}
                isOpen={activeStep === s.n}
                isCompleted={isStepComplete(s.n)}
                isEnabled={isEnabled(s.n)}
                summaryContent={getSummary(s.n)}
                onToggle={() => toggle(s.n)}
                onEdit={() => edit(s.n)}
              >
                {s.n === 1 && activeStep === 1 && (
                  <>
                    <StepOrderType />
                    <ContinueBtn disabled={!orderType} onClick={() => next(1)} />
                  </>
                )}
                {s.n === 2 && activeStep === 2 && <StepPickupAddress onContinue={() => next(2)} />}
                {s.n === 3 && activeStep === 3 && <StepServices onContinue={() => next(3)} />}
                {s.n === 4 && activeStep === 4 && <StepProvider onContinue={() => next(4)} />}
                {s.n === 5 && activeStep === 5 && <StepDelivery onContinue={() => next(5)} />}
                {s.n === 6 && activeStep === 6 && <StepPayment onContinue={() => next(6)} />}
                {s.n === 7 && activeStep === 7 && <StepReview onOrderConfirmed={() => setOrderConfirmed(true)} />}
              </AccordionStep>
            ))}
          </div>

          {/* Sticky sidebar */}
          {selectedServices?.length > 0 && activeStep >= 3 && (
            <div className='hidden w-96 shrink-0 lg:block'>
              <div className='sticky top-4'>
                <EstimateSidebar />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
